import { GameObjectReference, GameState } from "@shared/types";
import { css, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        max-width: 1024px;
        height: 100%;
        display: flex;
        flex-direction: column;
        max-height: 100vh;
    }

    .title {
        text-align: center;
        margin-top: 10px;
        overflow: auto;
    }

    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }

    .header img {
        width: 100%;
        height: auto;
        /* image-rendering: pixelated; */
        border-radius: 10px;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content {
        flex-grow: 1;
        overflow: auto;
        margin-top: 20px;
        padding: 0 10px;
    }

    .content p {
        margin: 0 0 10px 0;
    }

    .content p:last-of-type {
        margin: 0;
    }

    .footer {
        border-radius: 10px 10px 0 0;
        background-color: #001f54;
        border: 1px solid #001f54;
        margin-top: 10px;
        display: flex;
        height: 105px;
    }

    .footer .buttons {
        display: flex;
        flex-direction: column;
        overflow: auto;
        padding: 10px 10px 0 10px;
    }

    .footer .button {
        background-color: #034078;
        border: 1px solid #034078;
        padding: 5px 10px;
        margin: 0 0 10px 10px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: #075398;
    }

    .content input[type="number"],
    .content input[type="submit"] {
        background-color: #034078;
        border: 1px solid #034078;
        padding: 5px 10px;
        margin: 0 0 10px 10px;
        text-transform: uppercase;
        color: white;
        font-size: 16px;
        cursor: pointer;
        display: inline-block;
        user-select: none;
    }

    .content input[type="number"] {
        width: 150px;
        text-align: center;
    }

    .content input[type="submit"]:hover {
        background-color: #075398;
    }
`;

/**
 * Represents the Canvas page
 */
export class InputComponent extends HTMLElement {
    private isCodeCorrect: boolean = false;
    private isCodeIncorrect: boolean = false;

    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderContent()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);

        // Event listeners toevoegen na rendering
        this.attachEventListeners();
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content">
                ${
                    this.isCodeCorrect
                        ? `<a>Well it looks like you really do know the code.</a> <br>
                            <a>You may now enter the castle, press the <span style='color:red'>sneak</span> button to go through.</a>`
                        : `<a>Woah, you think you can sneak past me?</a> <br>
                            <a>Nobody gets past me unless you have the code...</a> <br>
                            <a>(8 x 5 + 20) ÷ 4 - 3 = ?</a> <br> <br>
                            <input type="number" id="numberInput" name="number">
                            <input type="submit" id="submitButton" value="Submit">`
                }
    
                ${this.isCodeIncorrect ? "<p style=\"color: red;\">Wrong code, try again.</p>" : ""}
            </div>
        `;
    }

    private attachEventListeners(): void {
        if (!this.shadowRoot) return;

        const inputElement: HTMLInputElement | null =
            this.shadowRoot.querySelector<HTMLInputElement>("#numberInput");
        const submitButton: HTMLButtonElement | null =
            this.shadowRoot.querySelector<HTMLButtonElement>("#submitButton");

        if (inputElement && submitButton) {
            submitButton.addEventListener("click", async () => {
                const inputValue: string = inputElement.value.trim();

                if (!inputValue) {
                    console.warn("Geen code ingevoerd.");
                    return;
                }

                console.log("Ingevoerde waarde:", inputValue);

                const isCodeCorrect: boolean = await this._gameRouteService.checkCode(inputValue);

                if (isCodeCorrect) {
                    console.log("Correcte code!");
                    this.isCodeCorrect = true;
                    this.isCodeIncorrect = false;
                    this.render();
                    await this._gameRouteService.handleCheckCode();
                }
                else {
                    console.log("Foute code.");
                    this.isCodeIncorrect = true;
                    this.render();
                }
            });
        }
    }
}
